import React, { useEffect, useState, useCallback } from "react";

// Lista de imágenes disponibles en public/images/
const PUZZLE_IMAGES = [
  "/images/puzzle1.jpg",
  "/images/puzzle2.jpg",
  "/images/puzzle3.jpg",
  "/images/puzzle4.jpg",
  "/images/puzzle5.jpg",
  "/images/puzzle6.jpg",
  "/images/puzzle7.jpg",
  "/images/puzzle8.jpg",
];

// URLs de respaldo si las imágenes locales no existen
const FALLBACK_IMAGES = [
  "https://picsum.photos/400/400?random=1",
  "https://picsum.photos/400/400?random=2",
  "https://picsum.photos/400/400?random=3",
  "https://picsum.photos/400/400?random=4",
  "https://picsum.photos/400/400?random=5",
  "https://picsum.photos/400/400?random=6",
  "https://picsum.photos/400/400?random=7",
  "https://picsum.photos/400/400?random=8",
];

function createSolvedTiles(gridSize) {
  return Array.from({ length: gridSize * gridSize }, (_, i) => i);
}

function getNeighbors(index, gridSize) {
  const row = Math.floor(index / gridSize);
  const col = index % gridSize;
  const neighbors = [];

  // arriba
  if (row > 0) neighbors.push(index - gridSize);
  // abajo
  if (row < gridSize - 1) neighbors.push(index + gridSize);
  // izquierda
  if (col > 0) neighbors.push(index - 1);
  // derecha
  if (col < gridSize - 1) neighbors.push(index + 1);

  return neighbors;
}

function shuffleTiles(gridSize, shuffleMoves = 200) {
  // empezamos desde resuelto y hacemos movimientos válidos al azar
  let tiles = createSolvedTiles(gridSize);
  let emptyIndex = tiles.length - 1; // el último es el "hueco"

  for (let i = 0; i < shuffleMoves; i++) {
    const neighbors = getNeighbors(emptyIndex, gridSize);
    const randomNeighbor =
      neighbors[Math.floor(Math.random() * neighbors.length)];

    // intercambiar pieza vecina con el hueco
    [tiles[emptyIndex], tiles[randomNeighbor]] = [
      tiles[randomNeighbor],
      tiles[emptyIndex],
    ];
    emptyIndex = randomNeighbor;
  }

  // evitar el caso trivial de quedar resuelto
  const solved = createSolvedTiles(gridSize);
  const isSolved = tiles.every((t, i) => t === solved[i]);
  if (isSolved) {
    // si por casualidad quedó igual, rehacemos
    return shuffleTiles(gridSize, shuffleMoves);
  }

  return tiles;
}

function SlidingPuzzle({
  imageUrl,
  gridSize = 3,
  canPlay = true,
  onWin = () => {},
}) {
  const [tiles, setTiles] = useState(() => shuffleTiles(gridSize));
  const [isSolved, setIsSolved] = useState(false);
  const [hasTriggeredWin, setHasTriggeredWin] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [puzzlesCompleted, setPuzzlesCompleted] = useState(0);
  const [usedImages, setUsedImages] = useState([]);
  const maxPuzzlesPerRound = 5;

  const resetPuzzle = useCallback(() => {
    setTiles(shuffleTiles(gridSize));
    setIsSolved(false);
    setHasTriggeredWin(false);
  }, [gridSize]);

  // Función para seleccionar una nueva imagen aleatoria
  const selectRandomImage = useCallback(() => {
    // Filtrar imágenes no usadas
    const availableIndices = PUZZLE_IMAGES.map((_, idx) => idx).filter(idx => !usedImages.includes(idx));
    
    // Si se usaron todas, reiniciar
    if (availableIndices.length === 0) {
      setUsedImages([]);
      const randomIndex = Math.floor(Math.random() * PUZZLE_IMAGES.length);
      const localImage = PUZZLE_IMAGES[randomIndex];
      
      const img = new Image();
      img.onload = () => {
        setCurrentImage(localImage);
        setUsedImages([randomIndex]);
      };
      img.onerror = () => {
        setCurrentImage(FALLBACK_IMAGES[randomIndex]);
        setUsedImages([randomIndex]);
      };
      img.src = localImage;
      return;
    }

    const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
    const localImage = PUZZLE_IMAGES[randomIndex];
    
    const img = new Image();
    img.onload = () => {
      setCurrentImage(localImage);
      setUsedImages(prev => [...prev, randomIndex]);
    };
    img.onerror = () => {
      setCurrentImage(FALLBACK_IMAGES[randomIndex]);
      setUsedImages(prev => [...prev, randomIndex]);
    };
    img.src = localImage;
  }, [usedImages]);

  // Seleccionar imagen aleatoria cuando cambia canPlay o imageUrl
  useEffect(() => {
    // Reiniciar contador cuando cambia canPlay (nueva ronda)
    setPuzzlesCompleted(0);
    setUsedImages([]);
    
    // Si se proporciona imageUrl, usarlo directamente
    if (imageUrl) {
      setCurrentImage(imageUrl);
      return;
    }

    // Si no, seleccionar una imagen aleatoria
    selectRandomImage();
  }, [canPlay, imageUrl]);

  // rebarajar cuando cambie la imagen o el gridSize
  useEffect(() => {
    resetPuzzle();
  }, [currentImage, gridSize, resetPuzzle]);

  const handleTileClick = (index) => {
    if (!canPlay || isSolved) return;

    const emptyTileValue = gridSize * gridSize - 1; // el valor del hueco
    const emptyIndex = tiles.indexOf(emptyTileValue);
    if (emptyIndex === -1) return;

    // ¿la pieza clickeada es vecina del hueco?
    const neighbors = getNeighbors(emptyIndex, gridSize);
    if (!neighbors.includes(index)) return;

    const newTiles = [...tiles];
    [newTiles[emptyIndex], newTiles[index]] = [
      newTiles[index],
      newTiles[emptyIndex],
    ];
    setTiles(newTiles);

    // comprobar si está resuelto
    const solvedTiles = createSolvedTiles(gridSize);
    const solved = newTiles.every((t, i) => t === solvedTiles[i]);
    if (solved) {
      setIsSolved(true);
      if (!hasTriggeredWin && puzzlesCompleted < maxPuzzlesPerRound) {
        setHasTriggeredWin(true);
        setPuzzlesCompleted(prev => prev + 1);
        onWin();
      }
    }
  };

  const handleNextPuzzle = () => {
    if (puzzlesCompleted >= maxPuzzlesPerRound) return;
    selectRandomImage();
    setHasTriggeredWin(false);
  };

  const tileSizePercent = 100 / gridSize;
  const emptyTileValue = gridSize * gridSize - 1;
  const canContinue = isSolved && puzzlesCompleted < maxPuzzlesPerRound;

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <span style={styles.title}>
          Rompecabezas deslizante ({puzzlesCompleted}/{maxPuzzlesPerRound})
        </span>
        <button style={styles.resetButton} type="button" onClick={resetPuzzle}>
          Reiniciar
        </button>
      </div>

      <div
        style={{
          ...styles.puzzle,
          gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
          gridTemplateRows: `repeat(${gridSize}, 1fr)`,
        }}
      >
        {tiles.map((tileValue, index) => {
          const isEmpty = tileValue === emptyTileValue;
          const row = Math.floor(tileValue / gridSize);
          const col = tileValue % gridSize;

          const backgroundPositionX =
            (col / (gridSize - 1 || 1)) * 100 + "%";
          const backgroundPositionY =
            (row / (gridSize - 1 || 1)) * 100 + "%";

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleTileClick(index)}
              disabled={!canPlay || isSolved || isEmpty}
              style={{
                ...styles.tile,
                cursor:
                  !canPlay || isSolved
                    ? "default"
                    : isEmpty
                    ? "default"
                    : "pointer",
                opacity: isEmpty ? 0 : 1,
                backgroundImage: isEmpty ? "none" : `url(${currentImage})`,
                backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                backgroundPosition: `${backgroundPositionX} ${backgroundPositionY}`,
              }}
            />
          );
        })}
      </div>

      <div style={styles.footer}>
        {!canPlay && (
          <span style={styles.info}>La ronda aún no está activa.</span>
        )}
        {isSolved && !canContinue && (
          <span style={styles.solved}>
            ¡Completado! {puzzlesCompleted}/{maxPuzzlesPerRound} rompecabezas 🎉
          </span>
        )}
        {canContinue && (
          <div style={styles.nextContainer}>
            <span style={styles.solved}>¡Completado! 🎉</span>
            <button 
              style={styles.nextButton} 
              type="button" 
              onClick={handleNextPuzzle}
            >
              Siguiente imagen ({puzzlesCompleted}/{maxPuzzlesPerRound})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    background: "rgba(0, 20, 40, 0.9)",
    borderRadius: 8,
    border: "1px solid rgba(0, 155, 255, 0.35)",
    padding: 8,
    fontSize: 12,
    color: "#E8F6FF",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  title: {
    fontWeight: 600,
    fontSize: 12,
  },
  resetButton: {
    borderRadius: 999,
    border: "none",
    padding: "4px 8px",
    fontSize: 11,
    cursor: "pointer",
    background: "rgba(0, 155, 255, 0.2)",
    color: "#5DB6FF",
  },
  puzzle: {
    width: "100%",
    maxWidth: 320,
    aspectRatio: "1 / 1",
    display: "grid",
    gap: 2,
    margin: "6px auto",
  },
  tile: {
    borderRadius: 4,
    border: "1px solid rgba(0, 100, 180, 0.6)",
    backgroundColor: "#020b16",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
    padding: 0,
    outline: "none",
  },
  footer: {
    marginTop: 4,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 11,
  },
  info: {
    color: "#A8D5FF",
  },
  solved: {
    color: "#FFE5A8",
    fontWeight: 600,
  },
  nextContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "center",
  },
  nextButton: {
    borderRadius: 999,
    border: "none",
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    background: "linear-gradient(135deg, #0077CC, #0099FF)",
    color: "#001A33",
    width: "100%",
  },
};

export default SlidingPuzzle;
